// src/app/routes/RequireRole.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useAuthSession } from "../features/auth/useAuth.js";

function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function normalizeRole(role) {
  return String(role || "").toUpperCase().trim();
}

export function RequireRole({ allow = ["ADMIN", "MODERATOR"], children }) {
  const { user, session, loading } = useAuthSession();
  const location = useLocation();

  const hasSession = !!session?.accessToken;

  // No session -> login
  if (!hasSession && !loading) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Session exists but user not loaded yet -> don't block routes
  if (hasSession && !user) {
    return children;
  }

  // Prefer role from user payload (most reliable)
  let role = normalizeRole(user?.role);

  // Fallbacks if your app stores it somewhere else
  if (!role) role = normalizeRole(session?.user?.role);

  // Fallback to JWT claim (support a few common names)
  if (!role && session?.accessToken) {
    const claims = parseJwt(session.accessToken) || {};
    role =
      normalizeRole(claims.role) ||
      normalizeRole(claims.user_role) ||
      normalizeRole(claims.userRole) ||
      normalizeRole(Array.isArray(claims.roles) ? claims.roles[0] : null);
  }

  const allowed = new Set(allow.map(normalizeRole));

  if (!role || !allowed.has(role)) {
    return <Navigate to="/app/trade" replace />;
  }

  return children;
}
